// WP-5.3 (M18 MF-1..3, S-26 `/explorer`): Money Flow Explorer — a full-width Sankey over
// `useMoneyFlow` (a straight passthrough to the engine's `queryMoneyFlow`), period picker,
// node-click right panel (MF-2), breadcrumb of applied drills. Only NODE clicks drill (not
// link/"segment" clicks): `recordsByNode` is node-scoped in the engine (MF-2's own "every
// important total explainable by its source records" is satisfied per-node already); a
// link-specific record slice would need a second, finer-grained aggregation the engine
// doesn't build — a documented scope trim, not a silent gap. Export-as-image (MF-3) is
// listed MAY, not SHALL — left out.
import { FLOW_LEVELS, formatMoney, fromMinor, type CurrencyCode } from '@pfos/engine';
import type { FlowLink, FlowNode, FlowRecordRef, MoneyFlowResponse } from '@pfos/shared';
import type { EChartsCoreOption } from 'echarts';
import { useMemo, useState } from 'react';
import { EChart } from '../components/EChart';
import { selectClass } from '../components/FormKit';
import { useMoneyFlow } from '../lib/flow';

type PeriodMode = 'month' | 'year';

function currentPeriod(mode: PeriodMode): { from: string; to: string } {
  const now = new Date();
  if (mode === 'month') {
    return {
      from: new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString(),
      to: new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1)).toISOString(),
    };
  }
  return {
    from: new Date(Date.UTC(now.getUTCFullYear(), 0, 1)).toISOString(),
    to: new Date(Date.UTC(now.getUTCFullYear() + 1, 0, 1)).toISOString(),
  };
}

// 18.7: "node color by level" — one color per FLOW_LEVELS index (client, serviceCategory,
// paymentChannel, fees, account, transfersOrSavings, terminal).
const LEVEL_COLORS = ['#2563eb', '#7c3aed', '#0891b2', '#d97706', '#16a34a', '#db2777', '#64748b'];

const FLOW_LEVEL_LABELS: Record<(typeof FLOW_LEVELS)[number], string> = {
  client: 'Client',
  serviceCategory: 'Service Category',
  paymentChannel: 'Payment Channel',
  fees: 'Fees',
  account: 'Account',
  transfersOrSavings: 'Transfers / Savings',
  terminal: 'Expense Categories / Savings / People Paid / Remaining',
};

// 18.7: "min link render 0.5%, others-bucket per level" — a chart-display concern only; the
// engine's own `flow.nodes`/`recordsByNode` stay the full, un-bucketed truth (AR-3), so
// clicking an "Others" bucket can still recover every constituent's real records.
const MIN_NODE_SHARE = 0.005;

interface ChartData {
  nodes: { name: string; depth: number; itemStyle: { color: string } }[];
  links: { source: string; target: string; value: number }[];
  labels: Record<string, string>;
  othersConstituents: Record<string, string[]>;
}

export function buildChartData(flow: MoneyFlowResponse): ChartData {
  const levelTotals = new Map<number, number>();
  for (const n of flow.nodes)
    levelTotals.set(n.level, (levelTotals.get(n.level) ?? 0) + n.valueMinor);

  const displayIdFor = new Map<string, string>();
  const othersConstituents: Record<string, string[]> = {};
  const kept: FlowNode[] = [];

  for (const n of flow.nodes) {
    const levelTotal = levelTotals.get(n.level) ?? 0;
    const share = levelTotal > 0 ? n.valueMinor / levelTotal : 1;
    if (share < MIN_NODE_SHARE) {
      const othersId = `others:${String(n.level)}`;
      displayIdFor.set(n.id, othersId);
      const existing = othersConstituents[othersId];
      if (existing !== undefined) existing.push(n.id);
      else othersConstituents[othersId] = [n.id];
    } else {
      displayIdFor.set(n.id, n.id);
      kept.push(n);
    }
  }

  const labels: Record<string, string> = {};
  const nodes: ChartData['nodes'] = kept.map((n) => {
    labels[n.id] = n.label;
    return {
      name: n.id,
      depth: n.level,
      itemStyle: { color: LEVEL_COLORS[n.level % LEVEL_COLORS.length] ?? '#64748b' },
    };
  });
  for (const [othersId, ids] of Object.entries(othersConstituents)) {
    const level = flow.nodes.find((n) => n.id === ids[0])?.level ?? 0;
    labels[othersId] = `Others (${String(ids.length)})`;
    nodes.push({
      name: othersId,
      depth: level,
      itemStyle: { color: LEVEL_COLORS[level % LEVEL_COLORS.length] ?? '#64748b' },
    });
  }

  const linkTotals = new Map<string, number>();
  for (const l of flow.links) {
    const source = displayIdFor.get(l.source) ?? l.source;
    const target = displayIdFor.get(l.target) ?? l.target;
    if (source === target) continue;
    const key = `${source} ${target}`;
    linkTotals.set(key, (linkTotals.get(key) ?? 0) + l.valueMinor);
  }
  const links: FlowLink[] = [...linkTotals.entries()].map(([key, valueMinor]) => {
    const [source = '', target = ''] = key.split(' ');
    return { source, target, valueMinor };
  });

  return {
    nodes,
    links: links.map((l) => ({ source: l.source, target: l.target, value: l.valueMinor })),
    labels,
    othersConstituents,
  };
}

export function recordsForDisplayNode(
  displayId: string,
  flow: MoneyFlowResponse,
  othersConstituents: Record<string, string[]>,
): FlowRecordRef[] {
  const constituentIds = othersConstituents[displayId] ?? [displayId];
  const seen = new Set<string>();
  const out: FlowRecordRef[] = [];
  for (const id of constituentIds) {
    for (const ref of flow.recordsByNode[id] ?? []) {
      const key = `${ref.kind}:${ref.record.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(ref);
    }
  }
  return out;
}

function describeRecord(ref: FlowRecordRef): string {
  if (ref.kind === 'transaction') {
    const t = ref.record;
    const who = t.party ?? t.category ?? (t.type === 'income' ? 'Income' : 'Expense');
    return `${t.type === 'income' ? 'Income' : 'Expense'} — ${who}`;
  }
  return `Transfer — ${ref.record.fromAccountId} → ${ref.record.toAccountId}`;
}

export function FlowExplorerPage(): React.JSX.Element {
  const [currency, setCurrency] = useState<CurrencyCode>('PKR');
  const [periodMode, setPeriodMode] = useState<PeriodMode>('year');
  const [history, setHistory] = useState<string[]>([]);

  const period = currentPeriod(periodMode);
  const { data: flow, isLoading } = useMoneyFlow({ currency, from: period.from, to: period.to });

  const chart = useMemo(() => (flow !== undefined ? buildChartData(flow) : undefined), [flow]);

  const selectedId = history[history.length - 1];

  const handleNodeClick = (name: string): void => {
    setHistory((prev) => {
      const idx = prev.indexOf(name);
      if (idx !== -1) return prev.slice(0, idx + 1);
      return [...prev, name];
    });
  };

  const option: EChartsCoreOption | undefined = useMemo(() => {
    if (chart === undefined) return undefined;
    return {
      tooltip: {
        formatter: (params: {
          dataType?: string;
          name?: string;
          value?: number;
          data?: { source?: string; target?: string };
        }) => {
          if (params.dataType === 'edge') {
            const source =
              params.data?.source !== undefined
                ? (chart.labels[params.data.source] ?? params.data.source)
                : '';
            const target =
              params.data?.target !== undefined
                ? (chart.labels[params.data.target] ?? params.data.target)
                : '';
            return `${source} → ${target}<br/>${formatMoney(fromMinor(currency, params.value ?? 0))}`;
          }
          const label = params.name !== undefined ? (chart.labels[params.name] ?? params.name) : '';
          return `${label}<br/>${formatMoney(fromMinor(currency, params.value ?? 0))}`;
        },
      },
      series: [
        {
          type: 'sankey',
          data: chart.nodes,
          links: chart.links,
          emphasis: { focus: 'adjacency' },
          label: {
            formatter: (p: { name?: string }) =>
              p.name !== undefined ? (chart.labels[p.name] ?? p.name) : '',
          },
          lineStyle: { color: 'gradient', opacity: 0.4 },
        },
      ],
    };
  }, [chart, currency]);

  const selectedRecords =
    flow !== undefined && chart !== undefined && selectedId !== undefined
      ? recordsForDisplayNode(selectedId, flow, chart.othersConstituents)
      : [];
  const selectedLabel =
    selectedId !== undefined && chart !== undefined
      ? (chart.labels[selectedId] ?? selectedId)
      : undefined;

  return (
    <div>
      <h1 className="mb-1 text-xl font-semibold text-ink">Flow Explorer</h1>
      <p className="mb-4 text-xs text-muted">
        {periodMode === 'month' ? 'This month' : 'This year'} · {currency} · fixed level order:{' '}
        {FLOW_LEVELS.map((l) => FLOW_LEVEL_LABELS[l]).join(' → ')}
      </p>

      <div className="mb-4 flex flex-wrap items-end gap-4">
        <label className="block">
          <span className="mb-1 block text-sm text-muted">Currency</span>
          <select
            className={selectClass}
            value={currency}
            onChange={(e) => {
              setCurrency(e.target.value as CurrencyCode);
              setHistory([]);
            }}
          >
            {['PKR', 'USD', 'EUR', 'GBP'].map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm text-muted">Period</span>
          <select
            className={selectClass}
            value={periodMode}
            onChange={(e) => {
              setPeriodMode(e.target.value as PeriodMode);
              setHistory([]);
            }}
          >
            <option value="month">This month</option>
            <option value="year">This year</option>
          </select>
        </label>
      </div>

      <nav aria-label="Drill breadcrumb" className="mb-3 flex flex-wrap items-center gap-1 text-sm">
        <button
          type="button"
          className="text-primary hover:underline"
          onClick={() => {
            setHistory([]);
          }}
        >
          Flow Explorer
        </button>
        {history.map((id, i) => (
          <span key={id} className="flex items-center gap-1">
            <span className="text-muted">/</span>
            <button
              type="button"
              className={
                i === history.length - 1 ? 'font-medium text-ink' : 'text-primary hover:underline'
              }
              onClick={() => {
                setHistory((prev) => prev.slice(0, i + 1));
              }}
            >
              {chart?.labels[id] ?? id}
            </button>
          </span>
        ))}
      </nav>

      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="min-w-0 flex-1">
          {option !== undefined && (chart?.nodes.length ?? 0) > 0 ? (
            <EChart
              option={option}
              onNodeClick={handleNodeClick}
              height={520}
              ariaLabel="Money flow sankey diagram"
            />
          ) : (
            !isLoading && <p className="text-sm text-muted">No flow for this period.</p>
          )}
        </div>

        {selectedId !== undefined && (
          <div className="rounded-[var(--radius-card)] border border-border bg-surface p-3 lg:w-80 lg:shrink-0">
            <p className="mb-2 text-sm font-medium text-ink">{selectedLabel}</p>
            {selectedRecords.length === 0 ? (
              <p className="text-sm text-muted">
                No source records (a computed total, e.g. Remaining).
              </p>
            ) : (
              <ul className="space-y-2">
                {selectedRecords.map((ref) => (
                  <li
                    key={`${ref.kind}:${ref.record.id}`}
                    className="border-b border-border pb-2 text-sm last:border-0"
                  >
                    <p className="text-ink">{describeRecord(ref)}</p>
                    <p className="text-xs text-muted">
                      {ref.kind === 'transaction' ? ref.record.at : ref.record.initiatedAt} ·{' '}
                      {formatMoney(
                        fromMinor(
                          currency,
                          ref.kind === 'transaction'
                            ? ref.record.amountMinor
                            : ref.record.sentMinor,
                        ),
                      )}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
